import { Link } from "react-router-dom";
import { Mail, Globe, Phone } from "lucide-react";
import Logo from "@/components/Logo";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

export default function Footer() {
  return (
    <footer className="bg-slate-900 text-slate-300" data-testid="site-footer">
      <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
        <div className="grid gap-10 md:grid-cols-3">
          <div>
            <Logo dark />
            <p className="mt-4 max-w-xs text-sm leading-relaxed text-slate-400">
              AI Voice Receptionist for modern businesses.
            </p>
            <p className="mt-6 text-xs text-slate-500">
              © {new Date().getFullYear()} CogniTask. All rights reserved.
            </p>
          </div>

          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider text-white">Navigate</h3>
            <ul className="mt-4 space-y-3 text-sm">
              <li><Link to="/" data-testid="footer-home-link" className="transition-colors hover:text-white">Home</Link></li>
              <li><Link to="/use-cases" data-testid="footer-use-cases-link" className="transition-colors hover:text-white">Use Cases</Link></li>
              <li><Link to="/contact" data-testid="footer-contact-link" className="transition-colors hover:text-white">Contact</Link></li>
              <li>
                <Dialog>
                  <DialogTrigger asChild>
                    <button type="button" data-testid="footer-privacy-link" className="transition-colors hover:text-white">Privacy Policy</button>
                  </DialogTrigger>
                  <DialogContent data-testid="privacy-policy-modal">
                    <DialogHeader>
                      <DialogTitle>Privacy Policy</DialogTitle>
                    </DialogHeader>
                    <p className="text-sm leading-relaxed text-slate-600">
                      CogniTask processes only the operational details needed to answer calls, schedule
                      appointments and respond to inquiries. We comply with the DPDP Act 2023, encrypt
                      form data in transit, and never sell your information to third parties. Call
                      recordings and transcripts are retained only as long as your business requires
                      them for operations.
                    </p>
                  </DialogContent>
                </Dialog>
              </li>
              <li>
                <Dialog>
                  <DialogTrigger asChild>
                    <button type="button" data-testid="footer-terms-link" className="transition-colors hover:text-white">Terms of Service</button>
                  </DialogTrigger>
                  <DialogContent data-testid="terms-modal">
                    <DialogHeader>
                      <DialogTitle>Terms of Service</DialogTitle>
                    </DialogHeader>
                    <p className="text-sm leading-relaxed text-slate-600">
                      CogniTask provides an AI voice receptionist service for inbound call handling,
                      FAQ resolution and appointment scheduling. The service works via conditional call
                      forwarding on your existing number. Businesses remain responsible for the accuracy
                      of the knowledge content (hours, pricing, services) they provide to the agent.
                    </p>
                  </DialogContent>
                </Dialog>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider text-white">Contact</h3>
            <ul className="mt-4 space-y-3 text-sm">
              <li className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-indigo-400" />
                <a href="mailto:support@cognitask.co.in" data-testid="footer-email-link" className="transition-colors hover:text-white">
                  support@cognitask.co.in
                </a>
              </li>
              <li className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-indigo-400" />
                <a href="tel:+919891350540" data-testid="footer-phone-link" className="transition-colors hover:text-white">
                  +91 98913 50540
                </a>
              </li>
              <li className="flex items-center gap-2">
                <Globe className="h-4 w-4 text-indigo-400" />
                <span>Serving India &amp; Global Markets</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="border-t border-slate-800" data-testid="footer-legal-banner">
        <div className="mx-auto max-w-7xl px-4 py-5 sm:px-6 lg:px-8">
          <p className="text-center text-xs leading-relaxed text-slate-500">
            CogniTask AI processes operational scheduling and inquiries. We adhere to the DPDP Act 2023
            and do not store sensitive payment card details or unencrypted patient records.
          </p>
        </div>
      </div>
    </footer>
  );
}
